import { Dispatch } from 'redux';
import { restActions } from "./slice";
import { makeApiRequest, RequestStatus } from '../api_request/api_request'
import { ErnotsAbstractModel, ModelClass, classNameMap, copyRestObject } from './models';
import type { ModelMap } from "./slice";
import { LoginState } from '../features/login/slice';
import { AppDispatch } from '../store/store';
import { makePlainObject } from '../utils';

/**
 * Downloads the full list of objects of a specific type. (The server excludes results that this user isn't allowed to see.)
 * The list is converted to a dictionary (using the key property) and sent to the Redux store.
 * 
 * @param modelClass The class whose objects are to be downloaded.
 *        Names of client-side model classes correspond to server-side interfaces.
 * @param dispatch The Redux dispatch function
 * @returns On success, the downloaded objects as a dictionary. Failure can be detected by examining the associated
 *     request object.
 */
export async function downloadDict(modelClass: ModelClass, appDispatch: AppDispatch, lifeCycleCallback: (rs: RequestStatus) => any)
{
    const mcn = classNameMap[modelClass.name];

    const status = await makeApiRequest(
    {
        endpoint: mcn,
        method: 'GET',
        dispatch: appDispatch,
        lifeCycleCallback,
    });
    
    if (status.success && Array.isArray(status.responseJson))
    {
        const models = {} as ModelMap;
        for (const x of status.responseJson)
        {
            const m = copyRestObject(x, modelClass);
            models[m.key] = m;
        }

        const objs = 
        {
            modelClassName: classNameMap[modelClass.name],
            models: models
        }
        
        appDispatch(restActions.setModels(objs));

        status.inState = true;
        lifeCycleCallback?.(makePlainObject(status));
    }

    return status;
}

/**
 * Downloads a single object from the server and sends it to the Redux store.
 * 
 * @param modelClass The class for the object.
 *        Names of client-side model classes correspond to server-side interfaces.
 * @param id Object's database ID.
 * @param dispatch The Redux dispatch function
 * @returns The downloaded object.
 */
export async function downloadObject(modelClass: ModelClass, id: string, dispatch: AppDispatch, lifeCycleCallback: (rs: RequestStatus) => any)
{
    const mcn = classNameMap[modelClass.name];
    const status = await makeApiRequest({
        endpoint: `${mcn}/${id}`,
        method: 'GET',
        dispatch,
        lifeCycleCallback
    });

    if (status.success && typeof status.responseJson === 'object')
    {
        const m = copyRestObject(status.responseJson, modelClass);
        dispatch(restActions.setModel(m));
        
        status.inState = true;
        lifeCycleCallback?.(makePlainObject(status));
    }

    return status;
}

/**
 * Sends an object to the server, using the verb "put" (update) or "post" (create), depending on whether
 * the object has a database ID.
 * 
 * @param obj The object to upload.
 * @param modelClass The class for the object.
 * @param dispatch The Redux dispatch function
 * @returns Whether the request succeeded.
 */
export async function uploadObject(obj: ErnotsAbstractModel, modelClass: ModelClass, dispatch: Dispatch, lifeCycleCallback: (rs: RequestStatus) => any)
{
    const method = obj.id ? 'PUT' : 'POST';
    const mcn = obj.modelClassName;
    const endpoint = obj.id ? `${mcn}/${obj.id}.json` : `${mcn}.json`;

    const status = await makeApiRequest({
        endpoint,
        method,
        postData: obj,
        dispatch,
        lifeCycleCallback
    });


    if (status.success)
    {
        const m = copyRestObject(status.responseJson, modelClass);
        dispatch(restActions.setModel(m));
        
        status.inState = true;
        lifeCycleCallback?.(makePlainObject(status));
    }

    return status;
}

/**
 * Delete an object
 * @param obj The object to delete.
 * @param dispatch The Redux dispatch function
 * @returns Whether the request succeeded.
 */
export async function deleteObject(obj:ErnotsAbstractModel, dispatch: Dispatch, lifeCycleCallback: (rs: RequestStatus) => any)
{
    if (!obj.id)
    {
        return;
    }

    const mcn = obj.modelClassName;
    const status = await makeApiRequest({
        endpoint: `${mcn}/${obj.id}/`,
        method: 'DELETE',
        dispatch,
        lifeCycleCallback
    });

    if (status.success)
    {
        dispatch(restActions.unsetModel(obj));
        
        status.inState = true;
        lifeCycleCallback?.(makePlainObject(status));
    }

    return status.success;
}

export function canEdit(obj: ErnotsAbstractModel, loginState: LoginState)
{
    return (loginState.currentUser.is_staff || !obj.public);
}